/**
 * Sequential multi-board onboarding queue.
 *
 * Wraps a single useOnboardJob lifecycle and feeds it one board at a time:
 * kick off board i, wait for its parent Windmill job to reach a terminal
 * phase, record the outcome, then start board i+1. A failed board never
 * blocks the rest of the queue.
 *
 * SEQUENTIAL IS A CORRECTNESS REQUIREMENT — each onboard parent job occupies
 * a Windmill worker for its whole duration while it polls its per-sprint
 * children, and the deployment has 3 workers. Firing >=3 parents at once can
 * consume every worker so no child can ever run (deadlock until the 30-min
 * child-poll timeouts unwind it). Do not parallelize without first
 * rearchitecting the onboard flow's child polling.
 */
import { computed, ref, watch } from "vue";

import { useOnboardJob, type OnboardPhase } from "@/composables/useOnboardJob";

export type QueueBoardState = "queued" | "running" | "done" | "failed";

export interface QueueBoardInput {
  board_id: number;
  name: string;
}

export interface QueueItem {
  board_id: number;
  name: string;
  state: QueueBoardState;
  error: string | null;
}

export function useOnboardQueue() {
  // One job lifecycle, reused board after board. The view binds to `job`
  // for the currently-running board's detailed progress (children, etc.).
  const job = useOnboardJob();

  const items = ref<QueueItem[]>([]);
  const currentIndex = ref(-1);
  const queueRunning = ref(false);

  const currentItem = computed<QueueItem | null>(
    () => items.value[currentIndex.value] ?? null,
  );

  /** Resolves on the next transition into a terminal phase. Must be armed
   * BEFORE job.start() — start() resolves at kickoff, not at completion, and
   * on kickoff failure it transitions to "error" before resolving.
   * If the owning component unmounts mid-queue, useOnboardJob's scope dispose
   * freezes the phase, this promise stays pending, and the queue simply never
   * advances — harmless, since the component (and queue state) are gone. */
  function nextTerminal(): Promise<OnboardPhase> {
    return new Promise((resolve) => {
      const stop = watch(job.phase, (p) => {
        if (p === "done" || p === "error") {
          stop();
          resolve(p);
        }
      });
    });
  }

  async function startQueue(
    jiraBaseUrl: string,
    boards: QueueBoardInput[],
  ): Promise<void> {
    if (queueRunning.value || boards.length === 0) return;
    queueRunning.value = true;
    items.value = boards.map((b) => ({
      board_id: b.board_id,
      name: b.name,
      state: "queued" as const,
      error: null,
    }));

    try {
      for (let i = 0; i < items.value.length; i++) {
        currentIndex.value = i;
        items.value[i].state = "running";

        const terminal = nextTerminal();
        await job.start({
          jira_base_url: jiraBaseUrl,
          board_id: items.value[i].board_id,
        });
        const phase = await terminal;

        if (phase === "error") {
          items.value[i].state = "failed";
          items.value[i].error = job.errorMessage.value ?? "kickoff failed";
        } else if (job.job.value?.success === false) {
          items.value[i].state = "failed";
          items.value[i].error = job.job.value?.error_brief ?? "flow returned a failure";
        } else {
          items.value[i].state = "done";
        }
      }
    } finally {
      queueRunning.value = false;
    }
  }

  return {
    items,
    currentIndex,
    currentItem,
    queueRunning,
    startQueue,
    /** The underlying single-board lifecycle for the running board. */
    job,
  };
}
